import { Ionicons } from '@expo/vector-icons';
import { Link, LinkProps, usePathname, router } from 'expo-router';
import React, { ComponentProps, useCallback, useRef, useState } from 'react';
import {
  Image,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  View,
  useWindowDimensions,
} from 'react-native';
import Animated, {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  FadeIn,
  FadeOut,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ENTRY_SIDEBAR_LABEL } from '@/content/payments';
import { useMembership } from '@/hooks/use-membership';
import { useProviderProfile } from '@/hooks/use-provider';
import { useTerms } from '@/hooks/use-terms';
import { useThemeColor } from '@/hooks/use-theme-color';
import { useAuthStore } from '@/store/authStore';
import {
  isClientUser,
  isProviderUser,
  // hasDualProfile, // desativado: prestador poder ser cliente
} from '@/utils/user-role';
import { Logo } from './logo';
import { ThemedText } from './themed-text';
import { ProviderNotificationBell } from './provider-notification-bell';
import { ProfessionalSwitcher } from './team/professional-switcher';
import { Tooltip } from './ui/tooltip';

type IoniconName = ComponentProps<typeof Ionicons>['name'];

type NavItem = {
  label: string;
  icon: IoniconName;
  href: LinkProps['href'];
  badge?: number;
  group?: string;
};

type NavGroup = {
  label: string;
  items: NavItem[];
};

// Larguras fixas para melhor controle em todas as telas
const SIDEBAR_EXPANDED_WIDTH = 240; // px - logo e rótulos cabem sem sobrar coluna
const SIDEBAR_COLLAPSED_WIDTH = 72; // px - largura para apenas ícones

/** Drawer mobile nativo: fora do fluxo e acima do Stack (irmão posterior no layout). */
const MOBILE_DRAWER_OVERLAY_STYLE = {
  position: 'absolute' as const,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1000,
  elevation: 10,
};
const MOBILE_DRAWER_MENU_STYLE = {
  position: 'absolute' as const,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1001,
  elevation: 11,
};

export function DashboardSidebar({
  isOpen,
  onClose,
  isCollapsed: isCollapsedProp = false,
  onToggleCollapse,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  children?: React.ReactNode;
}) {
  const { user, logout } = useAuthStore();
  const { width, height } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const pathname = usePathname();
  const isMobile = width < 768;
  /**
   * Recolhimento é coisa de desktop. O estado agora é persistido, então sem
   * este piso um desktop recolhido reabriria o drawer mobile só com ícones.
   */
  const isCollapsed = !isMobile && isCollapsedProp;
  const cardBackground = useThemeColor('card');
  const borderColor = useThemeColor('border');

  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuTriggerRef = useRef<View>(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);

  const progress = useSharedValue(isMobile ? (isOpen ? 1 : 0) : 1);
  const collapseProgress = useSharedValue(isCollapsed ? 1 : 0);

  React.useEffect(() => {
    if (isMobile) {
      progress.value = withTiming(isOpen ? 1 : 0, { duration: 300 });
    }
  }, [isOpen, isMobile, progress]);

  React.useEffect(() => {
    if (!isMobile) {
      collapseProgress.value = withTiming(isCollapsed ? 1 : 0, { duration: 300 });
    }
  }, [isCollapsed, isMobile, collapseProgress]);

  // Largura fixa em pixels
  const sidebarWidth = SIDEBAR_EXPANDED_WIDTH;
  const sidebarCollapsedWidth = SIDEBAR_COLLAPSED_WIDTH;

  const animatedSidebarStyle = useAnimatedStyle(() => {
    if (!isMobile) {
      const animatedWidth = interpolate(
        collapseProgress.value,
        [0, 1],
        [sidebarWidth, sidebarCollapsedWidth],
      );
      return {
        width: animatedWidth,
      };
    }
    return {
      transform: [
        {
          translateX: interpolate(progress.value, [0, 1], [-sidebarWidth, 0]),
        },
      ],
    };
  });

  const animatedOverlayStyle = useAnimatedStyle(() => {
    return {
      opacity: progress.value,
      pointerEvents: progress.value > 0 ? 'auto' : 'none',
    };
  });

  const animatedMobileMenuStyle = useAnimatedStyle(() => {
    return {
      opacity: progress.value,
      transform: [
        {
          translateY: interpolate(progress.value, [0, 1], [-12, 0]),
        },
      ],
      pointerEvents: progress.value > 0 ? 'auto' : 'none',
    };
  });

  const isProvider = isProviderUser(user);
  // Prestador prevalece: front não mescla mais nav de cliente quando usuário também é prestador
  const isClient = isClientUser(user);

  const terms = useTerms({ enabled: isProvider });
  const { data: providerProfile } = useProviderProfile({ enabled: isProvider });
  // Papel e modo decidem o menu: solo e dono veem tudo de hoje; "Equipe" só
  // para o dono de organização; profissional vê gastos só no "cada um por si".
  const { can } = useMembership();

  // Navegação Principal
  const mainNav: NavItem[] = [];

  if (isProvider) {
    mainNav.push(
      {
        label: 'Início',
        icon: 'home-outline',
        href: '/(tabs)/(provider)/home',
      },
      {
        label: terms.title.servicePlural,
        icon: 'briefcase-outline',
        href: '/(tabs)/(provider)/services',
      },
      {
        label: 'Agenda',
        icon: 'calendar-outline',
        href: '/(tabs)/(provider)/schedule' as any,
      },
      {
        label: terms.title.enrollmentPlural,
        icon: 'document-text-outline',
        href: '/(tabs)/(provider)/enrollments',
      },
      {
        label: terms.title.clientPlural,
        icon: 'people-outline',
        href: '/(tabs)/(provider)/clients',
      },
      // O assistente permanente (bloco 6). Entra na navegação principal, e não
      // como FAB: ele é um lugar do app, não um botão flutuante de uma tela.
      {
        label: 'Assistente',
        icon: 'sparkles-outline',
        href: '/(tabs)/(provider)/assistant' as any,
      },
      {
        label: 'Perfil público',
        icon: 'globe-outline',
        href: '/(tabs)/(provider)/profile/public' as any,
      },
    );
    if (can('team')) {
      mainNav.push({
        label: 'Equipe',
        icon: 'people-circle-outline',
        href: '/(tabs)/(provider)/team' as any,
      });
    }
    // Cadastro retroativo existe como rota, mas fica fora da sidebar.
  }

  if (isClient) {
    mainNav.push(
      {
        label: 'Início',
        icon: 'grid-outline',
        href: '/(tabs)/(client)/hoje',
      },
      {
        label: 'Meus Contratos',
        icon: 'document-text-outline',
        href: '/(tabs)/(client)/enrollments-client',
      },
      {
        label: 'Meus Agendamentos',
        icon: 'calendar-outline',
        href: '/(tabs)/(client)/appointments' as any,
      },
      {
        label: 'Pagamentos',
        icon: 'card-outline',
        href: '/(tabs)/(client)/payments',
      },
    );
  }

  // Navegação Financeira (apenas para Provider)
  const financialNav: NavGroup | null = isProvider
    ? {
        label: 'Financeiro',
        items: [
          {
            label: 'Relatórios',
            icon: 'bar-chart-outline',
            href: '/(tabs)/(provider)/reports',
          },
          ...(can('expenses')
            ? [
                {
                  label: 'Gastos',
                  icon: 'wallet-outline',
                  href: '/(tabs)/(provider)/expenses',
                } satisfies NavItem,
              ]
            : []),
          // Disputas continua acessível pela rota, mas some da sidebar.
          {
            label: 'Cobranças',
            icon: 'card-outline',
            href: '/(tabs)/(provider)/charges',
          },
          // "Recebimentos" (T6.1): sempre visível para o prestador — sem
          // conta ativa, a tela leva para `payments/activate.tsx`, que explica
          // o recebimento automático e começa a ativação.
          {
            label: ENTRY_SIDEBAR_LABEL,
            icon: 'trending-up-outline',
            href: '/(tabs)/(provider)/payments' as LinkProps['href'],
          },
        ],
      }
    : null;

  const closeUserMenu = useCallback(() => {
    setShowUserMenu(false);
    setUserMenuAnchor(null);
  }, []);

  const openUserMenu = useCallback(() => {
    const node = userMenuTriggerRef.current as any;
    if (!node) {
      setShowUserMenu(true);
      return;
    }

    const applyAnchorAndOpen = (x: number, y: number, w: number, h: number) => {
      setUserMenuAnchor({ x, y, width: w, height: h });
      setShowUserMenu(true);
    };

    if (Platform.OS === 'web') {
      const el = node.getBoundingClientRect ? node : node;
      const rect =
        typeof el?.getBoundingClientRect === 'function' ? el.getBoundingClientRect() : null;
      if (rect) {
        applyAnchorAndOpen(rect.left, rect.top, rect.width, rect.height);
      } else {
        setShowUserMenu(true);
      }
      return;
    }

    node.measureInWindow?.((x: number, y: number, w: number, h: number) => {
      applyAnchorAndOpen(x, y, w, h);
    });
    if (typeof node.measureInWindow !== 'function') {
      setShowUserMenu(true);
    }
  }, []);

  const handleLogout = () => {
    onClose();
    closeUserMenu();
    logout();
  };

  const handleNavigateToProfile = () => {
    closeUserMenu();
    onClose();
    // Navegar para a página de perfil baseado na role do usuário
    if (isProvider) {
      router.push('/(tabs)/(provider)/profile');
    } else if (isClient) {
      router.push('/(tabs)/(client)/account');
    }
  };

  const handleNavigateToSettings = () => {
    closeUserMenu();
    onClose();
    router.push('/(tabs)/(provider)/settings');
  };

  // usePathname() devolve a rota sem os grupos ("/services/123"); o href da
  // navegação carrega os grupos ("/(tabs)/(provider)/services"). Tira os grupos
  // do href para a página e as subpáginas dela acenderem o mesmo item.
  const isActive = (href: string) => {
    if (!href || !pathname) return false;
    const normalizedPathname = pathname.replace(/\/$/, '');
    const normalizedHref = (href as string).replace(/\/\([^/]+\)/g, '').replace(/\/$/, '');
    return (
      normalizedPathname === normalizedHref || normalizedPathname.startsWith(normalizedHref + '/')
    );
  };

  /**
   * Quem cadastra pelo WhatsApp entra sem `username`: o nome do prestador é o
   * `title` (nome do negócio, gravado no onboarding), mesma regra do perfil.
   */
  const baseName = (isProvider ? providerProfile?.title?.trim() : '') || user?.username || '';

  const getUserDisplayName = () => {
    if (!baseName) return 'Usuário';
    if (isProvider && providerProfile?.title?.trim()) return baseName;
    const parts = baseName.split(' ');
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[parts.length - 1]}`;
  };

  const getUserInitials = () => {
    if (!baseName) return 'U';
    const parts = baseName.split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
  };

  // Componente do Avatar do Usuário
  const UserAvatar = ({ size = 40 }: { size?: number }) => {
    if (user?.image) {
      return (
        <Image
          source={{ uri: user.image }}
          aria-hidden
          style={{ width: size, height: size, borderRadius: size / 2 }}
        />
      );
    }
    return (
      <View
        style={{ width: size, height: size, borderRadius: size / 2 }}
        className="items-center justify-center bg-gradient-to-br from-primary to-primary-600"
      >
        <ThemedText
          style={{ fontSize: size * 0.4 }}
          className="font-semibold text-primary-foreground"
        >
          {getUserInitials()}
        </ThemedText>
      </View>
    );
  };

  // Menu dropdown do usuário (Desktop)
  const UserMenuDropdown = () => {
    if (!showUserMenu) return null;

    // Âncora acompanha o trigger onde quer que ele esteja (hoje, no rodapé da
    // sidebar): se não couber abaixo, o menu sobe e cola pela base do trigger.
    const spaceBelow = userMenuAnchor ? height - (userMenuAnchor.y + userMenuAnchor.height) : 0;
    const opensAbove = !!userMenuAnchor && spaceBelow < 260;

    const dropdownStyle = {
      backgroundColor: cardBackground,
      borderColor,
      borderWidth: 1,
      ...(userMenuAnchor
        ? {
            ...(opensAbove
              ? { bottom: height - userMenuAnchor.y + 4 }
              : { top: userMenuAnchor.y + userMenuAnchor.height + 4 }),
            right: width - userMenuAnchor.x - userMenuAnchor.width,
            ...(Platform.OS === 'web' ? ({ position: 'fixed' as const } as const) : {}),
          }
        : { top: 80, right: 16 }),
    };

    return (
      <Modal transparent visible={showUserMenu} onRequestClose={closeUserMenu}>
        <View className="flex-1">
          <Pressable
            className="flex-1"
            style={Platform.OS === 'web' ? { backgroundColor: 'rgba(0,0,0,0.15)' } : undefined}
            onPress={closeUserMenu}
          />
          <Animated.View
            entering={FadeIn.duration(150)}
            exiting={FadeOut.duration(100)}
            style={dropdownStyle}
            className="absolute z-[100] min-w-[200px] overflow-hidden rounded-xl shadow-xl"
          >
            {/* Header do menu */}
            <View className="border-b border-border p-4">
              <View className="flex-row items-center gap-3">
                <UserAvatar size={36} />
                <View className="flex-1">
                  <ThemedText numberOfLines={1} className="font-semibold text-sm text-foreground">
                    {getUserDisplayName()}
                  </ThemedText>
                  <ThemedText className="text-xs text-card-foreground">
                    {/* Dual profile (Prestador e Cliente) desativado: prestador sempre prevalece */}
                    {isProvider ? 'Prestador' : 'Cliente'}
                  </ThemedText>
                </View>
              </View>
            </View>

            {/* Menu items */}
            <View className="p-2">
              <Pressable
                onPress={handleNavigateToProfile}
                className="flex-row items-center gap-3 rounded-lg px-3 py-2.5 active:bg-neutral-100"
              >
                <Ionicons name="person-outline" size={18} className="text-card-foreground" />
                <ThemedText className="text-sm text-foreground">Meu Perfil</ThemedText>
              </Pressable>

              {isProvider && (
                <Pressable
                  onPress={handleNavigateToSettings}
                  className="flex-row items-center gap-3 rounded-lg px-3 py-2.5 active:bg-neutral-100"
                >
                  <Ionicons name="settings-outline" size={18} className="text-card-foreground" />
                  <ThemedText className="text-sm text-foreground">Configurações</ThemedText>
                </Pressable>
              )}

              <View className="my-2 h-px bg-border" />

              <Pressable
                onPress={handleLogout}
                className="flex-row items-center gap-3 rounded-lg px-3 py-2.5 active:bg-error-50"
              >
                <Ionicons name="log-out-outline" size={18} className="text-error-500" />
                <ThemedText className="text-sm text-error-500">Sair</ThemedText>
              </Pressable>
            </View>
          </Animated.View>
        </View>
      </Modal>
    );
  };

  const NavItemComponent = ({ item }: { item: NavItem }) => {
    const active = isActive(item.href as string);
    const link = (
      <Link href={item.href} asChild>
        <Pressable
          onPress={isMobile ? onClose : undefined}
          accessibilityState={{ selected: active }}
          className={`
            flex-row items-center ${isCollapsed ? 'justify-center px-2' : 'px-3'} mb-0.5 rounded-lg
            ${isMobile ? 'py-3' : 'py-2'}
            ${active ? 'bg-primary/10' : ''}
            active:bg-primary/5
          `}
        >
          {active && !isCollapsed && (
            <View className="absolute bottom-2 left-0 top-2 w-[3px] rounded-r-full bg-primary" />
          )}
          <Ionicons name={item.icon} size={isMobile ? 22 : 18} className="text-card-foreground" />
          {!isCollapsed && (
            <>
              <ThemedText
                numberOfLines={1}
                ellipsizeMode="tail"
                className={`ml-2.5 flex-1 ${isMobile ? '' : 'text-sm'} ${
                  active ? 'font-semibold text-primary' : 'text-foreground'
                }`}
              >
                {item.label}
              </ThemedText>
              {item.badge !== undefined && item.badge > 0 && (
                <View className="ml-2 rounded-full bg-primary px-1.5 py-0.5">
                  <ThemedText className="font-semibold text-[10px] text-primary-foreground">
                    {item.badge}
                  </ThemedText>
                </View>
              )}
            </>
          )}
        </Pressable>
      </Link>
    );

    if (isCollapsed) {
      return (
        <Tooltip side="right" content={item.label}>
          {link}
        </Tooltip>
      );
    }
    return link;
  };

  const NavGroupComponent = ({ group }: { group: NavGroup }) => {
    if (isCollapsed) {
      return (
        <View className="mb-4">
          <View className="mx-2 mb-2 h-px bg-border" />
          {group.items.map((item) => (
            <NavItemComponent key={item.href as string} item={item} />
          ))}
        </View>
      );
    }

    return (
      <View className="mb-4">
        <ThemedText
          className={`mb-1 font-semibold uppercase tracking-wider text-foreground/40 ${
            isMobile ? 'px-4 text-xs' : 'px-3 text-[10px]'
          }`}
        >
          {group.label}
        </ThemedText>
        {group.items.map((item) => (
          <NavItemComponent key={item.href as string} item={item} />
        ))}
      </View>
    );
  };

  // ==================== MOBILE ====================
  if (isMobile) {
    const mobileDrawerPanel = (
      <>
        <View
          className="flex-row items-center justify-between border-b border-border px-4 py-4"
          style={{ flexShrink: 0 }}
        >
          <Logo
            variant="full"
            size={48}
            color="tint"
            hasMargin={false}
            showIcon={true}
            showText={true}
          />
          <View className="flex-row items-center gap-1">
            {isProvider && <ProviderNotificationBell onClose={onClose} />}
            <Pressable
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel="Fechar menu"
              className="h-10 w-10 items-center justify-center rounded-full bg-neutral-100 active:bg-neutral-200"
            >
              <Ionicons name="close" size={22} className="text-foreground" />
            </Pressable>
          </View>
        </View>

        <ScrollView
          keyboardShouldPersistTaps="handled"
          className="flex-1"
          style={{ flexGrow: 1, flexShrink: 1, minHeight: 0 }}
          contentContainerStyle={{ paddingBottom: insets.bottom + 24 }}
          showsVerticalScrollIndicator={false}
        >
          <Pressable
            onPress={handleNavigateToProfile}
            className="mx-4 mt-4 flex-row items-center gap-4 rounded-2xl bg-gradient-to-r from-primary/5 to-primary/10 p-4 active:from-primary/10 active:to-primary/15"
          >
            <UserAvatar size={56} />
            <View className="flex-1">
              <ThemedText numberOfLines={1} className="font-semibold text-lg text-foreground">
                {getUserDisplayName()}
              </ThemedText>
              <ThemedText className="text-sm text-foreground/60">
                {/* Dual profile (Prestador e Cliente) desativado: prestador sempre prevalece */}
                {isProvider ? 'Prestador de Serviço' : 'Cliente'}
              </ThemedText>
            </View>
            <View className="h-8 w-8 items-center justify-center rounded-full bg-card/80">
              <Ionicons name="chevron-forward" size={18} className="text-foreground/50" />
            </View>
          </Pressable>

          {isProvider && (
            <Pressable
              onPress={handleNavigateToSettings}
              accessibilityRole="button"
              className="mx-4 mt-2 flex-row items-center gap-3 rounded-xl px-4 py-3 active:bg-neutral-100"
            >
              <Ionicons name="settings-outline" size={20} className="text-card-foreground" />
              <ThemedText className="flex-1 text-foreground">Configurações</ThemedText>
              <Ionicons name="chevron-forward" size={18} className="text-foreground/50" />
            </Pressable>
          )}

          <ProfessionalSwitcher variant="drawer" onPicked={onClose} />

          <View className="px-2 py-4">
            <View className="mb-4">
              {mainNav.map((item) => (
                <NavItemComponent key={item.href as string} item={item} />
              ))}
            </View>

            {financialNav && <NavGroupComponent group={financialNav} />}
          </View>

          <View className="mt-6 border-t border-border px-4 pb-8 pt-4">
            <Pressable
              onPress={handleLogout}
              className="flex-row items-center justify-center gap-2 rounded-xl bg-neutral-100 px-4 py-3 active:bg-neutral-200"
            >
              <Ionicons name="log-out-outline" size={20} className="text-foreground/70" />
              <ThemedText className="font-medium text-sm text-foreground/70">
                Sair da conta
              </ThemedText>
            </Pressable>
            <ThemedText className="mt-3 text-center text-[10px] text-foreground/30">
              v1.0.0
            </ThemedText>
          </View>
        </ScrollView>
      </>
    );

    return (
      <>
        {Platform.OS === 'web' ? (
          <Modal
            visible={isOpen}
            animationType="fade"
            presentationStyle="fullScreen"
            onRequestClose={onClose}
          >
            <View
              className="flex-1 bg-card"
              style={{
                paddingTop: insets.top,
                paddingBottom: insets.bottom,
                flexDirection: 'column',
              }}
            >
              {mobileDrawerPanel}
            </View>
          </Modal>
        ) : (
          <>
            <Animated.View
              style={[MOBILE_DRAWER_OVERLAY_STYLE, animatedOverlayStyle]}
              className="bg-black/60"
            >
              <Pressable onPress={onClose} className="flex-1" />
            </Animated.View>

            <Animated.View
              className="bg-card"
              style={[
                MOBILE_DRAWER_MENU_STYLE,
                {
                  paddingTop: insets.top,
                  paddingBottom: insets.bottom,
                  flexDirection: 'column',
                },
                animatedMobileMenuStyle,
              ]}
            >
              {mobileDrawerPanel}
            </Animated.View>
          </>
        )}
      </>
    );
  }

  // ==================== DESKTOP ====================
  // A sidebar é o único cromo do desktop: logo no topo (com o gatilho discreto
  // de recolher ao lado), navegação no meio, perfil + sino no rodapé.
  const collapseToggle = onToggleCollapse ? (
    <Pressable
      onPress={onToggleCollapse}
      accessibilityRole="button"
      accessibilityLabel={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
      className="h-7 w-7 items-center justify-center rounded-lg active:bg-neutral-100"
      hitSlop={6}
    >
      <Ionicons
        name={isCollapsed ? 'chevron-forward' : 'chevron-back'}
        size={14}
        className="text-foreground/40"
      />
    </Pressable>
  ) : null;

  const sidebarContent = (
    <Animated.View
      className="bg-background"
      style={[
        {
          paddingTop: insets.top,
          paddingBottom: insets.bottom,
          flexShrink: 0,
          flexGrow: 0,
          borderRightWidth: 1,
          borderRightColor: borderColor,
        },
        animatedSidebarStyle,
      ]}
    >
      {/* Identidade - logo e o gatilho de recolher, sem divisória */}
      <View
        className={`flex-row items-center ${isCollapsed ? 'justify-center px-2' : 'justify-between px-3'} py-4`}
      >
        {isCollapsed ? (
          <Logo
            variant="full"
            size={8}
            color="tint"
            hasMargin={false}
            showIcon={true}
            showText={false}
          />
        ) : (
          <>
            <Logo
              variant="full"
              size={34}
              color="tint"
              hasMargin={false}
              showIcon={true}
              showText={true}
            />
            {collapseToggle}
          </>
        )}
      </View>

      {isCollapsed && collapseToggle ? (
        <View className="mb-1 items-center">
          <Tooltip side="right" content="Expandir menu">
            {collapseToggle}
          </Tooltip>
        </View>
      ) : null}

      {/* Profissional em foco (dono de equipe) */}
      <ProfessionalSwitcher variant={isCollapsed ? 'collapsed' : 'expanded'} />

      {/* Navigation */}
      <ScrollView
        keyboardShouldPersistTaps="handled"
        className={`flex-1 ${isCollapsed ? 'px-1' : 'px-2'} py-2`}
        showsVerticalScrollIndicator={false}
      >
        {/* Main Navigation */}
        <View className="mb-4">
          {mainNav.map((item) => (
            <NavItemComponent key={item.href as string} item={item} />
          ))}
        </View>

        {/* Financial Navigation */}
        {financialNav && <NavGroupComponent group={financialNav} />}
      </ScrollView>

      {/* Footer - perfil e sino na mesma linha */}
      <View className={`border-t border-border ${isCollapsed ? 'items-center gap-1 p-2' : 'p-2'}`}>
        {isCollapsed ? (
          <>
            <View ref={userMenuTriggerRef} collapsable={false}>
              <Tooltip side="right" content={getUserDisplayName()}>
                <Pressable
                  onPress={openUserMenu}
                  className="items-center justify-center rounded-lg p-1 active:bg-neutral-100"
                >
                  <UserAvatar size={28} />
                </Pressable>
              </Tooltip>
            </View>
            {isProvider && (
              <Tooltip side="right" content="Notificações">
                <ProviderNotificationBell iconSize={20} />
              </Tooltip>
            )}
          </>
        ) : (
          <View className="flex-row items-center">
            <View ref={userMenuTriggerRef} collapsable={false} className="min-w-0 flex-1">
              <Pressable
                onPress={openUserMenu}
                className="flex-row items-center gap-2 rounded-lg p-1 active:bg-neutral-100"
              >
                <UserAvatar size={28} />
                <View className="min-w-0 flex-1">
                  <ThemedText
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    className="font-medium text-[13px] text-foreground"
                  >
                    {getUserDisplayName()}
                  </ThemedText>
                  <ThemedText numberOfLines={1} className="text-[11px] text-card-foreground">
                    {/* Dual profile (Prestador e Cliente) desativado: prestador sempre prevalece */}
                    {isProvider ? 'Prestador' : 'Cliente'}
                  </ThemedText>
                </View>
              </Pressable>
            </View>
            {isProvider && <ProviderNotificationBell iconSize={20} />}
          </View>
        )}
      </View>
    </Animated.View>
  );

  return (
    <View
      className="flex-1"
      style={
        Platform.OS === 'web'
          ? ({
              height: '100dvh',
              minHeight: '100dvh',
              overflow: 'hidden',
            } as const)
          : undefined
      }
    >
      <UserMenuDropdown />
      <View className="flex-1 flex-row" style={{ minHeight: 0 }}>
        {sidebarContent}
        <View className="flex-1" style={{ minHeight: 0 }}>
          {children}
        </View>
      </View>
    </View>
  );
}
